const freeze = (value) => Object.freeze(value);

const permissionEntry = ({ anyOf = [], allOf = [] } = {}) =>
  freeze({
    anyOf: freeze([...anyOf]),
    allOf: freeze([...allOf]),
  });

export const PLATFORM_ROUTE_PERMISSIONS = freeze({
  '/platform': permissionEntry({
    allOf: [
      'platform.tenants.dashboard.read',
      'platform.tenants.read',
      'platform.integrations.providers.read',
    ],
  }),
  '/platform/tenant/:id': permissionEntry({
    allOf: ['platform.tenants.read'],
  }),
  '/platform/saas-billing/catalog': permissionEntry({
    allOf: [
      'platform.catalog.plans.read',
      'platform.catalog.features.read',
      'platform.catalog.entitlements.read',
    ],
  }),
  '/platform/saas-billing/runtime/history': permissionEntry({
    allOf: ['platform.runtime.history.read'],
  }),
  '/platform/saas-billing/runtime/search': permissionEntry({
    allOf: ['platform.runtime.history.read'],
  }),
  '/platform/saas-billing/runtime/incidents': permissionEntry({
    allOf: ['platform.runtime.history.read', 'platform.runtime.preview.read'],
  }),
  '/platform/saas-billing/runtime/cockpit': permissionEntry({
    allOf: [
      'platform.runtime.diagnostics.read',
      'platform.runtime.scheduler.read',
      'platform.runtime.history.read',
    ],
  }),
  '/platform/saas-billing/runtime/provider-config': permissionEntry({
    allOf: ['platform.provider_config.read'],
  }),
  '/platform/saas-billing/runtime/recovery-operations': permissionEntry({
    allOf: ['platform.recovery.read'],
  }),
  '/platform/saas/subscription-change-requests': permissionEntry({
    allOf: ['platform.subscription_requests.read'],
  }),
  '/platform/saas/timeline': permissionEntry({
    allOf: ['platform.operational_events.timeline.read'],
  }),
  '/platform/saas/operational-events': permissionEntry({
    allOf: ['platform.operational_events.read'],
  }),
  '/platform/saas/operational-health': permissionEntry({
    allOf: ['platform.readiness.health.read'],
  }),
  '/platform/iam': permissionEntry({
    anyOf: ['platform.iam.users.read', 'platform.iam.roles.read'],
  }),
  '/platform/saas-billing/operations': permissionEntry({
    allOf: ['platform.billing.operations.read', 'platform.billing.webhooks.read'],
  }),
  '/platform/saas/subscriptions-dashboard': permissionEntry({
    allOf: ['platform.tenants.dashboard.read', 'platform.tenants.read'],
  }),
  '/platform/saas/alert-center': permissionEntry({
    allOf: ['platform.alerts.read'],
  }),
  '/platform/saas/feature-flags': permissionEntry({
    allOf: ['platform.feature_flags.read'],
  }),
  '/platform/saas/go-live-readiness': permissionEntry({
    allOf: ['platform.readiness.read'],
  }),
});

export const PLATFORM_ACTION_PERMISSIONS = freeze({
  'tenant.create': permissionEntry({ allOf: ['platform.tenants.create'] }),
  'tenant.update': permissionEntry({ allOf: ['platform.tenants.update'] }),
  'tenant.delete': permissionEntry({ allOf: ['platform.tenants.delete'] }),
  'tenant.admin_users.manage': permissionEntry({ allOf: ['platform.tenants.admin_users.manage'] }),

  'catalog.plans.create': permissionEntry({ allOf: ['platform.billing.catalog.plans.create'] }),
  'catalog.plans.status.write': permissionEntry({ allOf: ['platform.billing.catalog.plans.status.write'] }),
  'catalog.plans.is_public.write': permissionEntry({ allOf: ['platform.billing.catalog.plans.is_public.write'] }),
  'catalog.features.write': permissionEntry({ allOf: ['platform.billing.catalog.features.write'] }),
  'catalog.features.status.write': permissionEntry({ allOf: ['platform.billing.catalog.features.status.write'] }),
  'catalog.entitlements.create': permissionEntry({ allOf: ['platform.billing.catalog.entitlements.create'] }),
  'catalog.entitlements.update': permissionEntry({ allOf: ['platform.billing.catalog.entitlements.update'] }),
  'catalog.entitlements.delete': permissionEntry({ allOf: ['platform.billing.catalog.entitlements.delete'] }),

  'subscription.approve': permissionEntry({ allOf: ['platform.subscription_requests.approve'] }),
  'subscription.reject': permissionEntry({ allOf: ['platform.subscription_requests.reject'] }),
  'subscription.prepare_payment': permissionEntry({ allOf: ['platform.subscription_requests.prepare_payment'] }),

  'alerts.evaluate': permissionEntry({ allOf: ['platform.alerts.evaluate'] }),
  'alerts.acknowledge': permissionEntry({ allOf: ['platform.alerts.acknowledge'] }),
  'alerts.resolve': permissionEntry({ allOf: ['platform.alerts.resolve'] }),

  'feature_flags.write': permissionEntry({ allOf: ['platform.feature_flags.write'] }),
  'feature_flags.toggle': permissionEntry({ allOf: ['platform.feature_flags.toggle'] }),
  'feature_flags.resolve': permissionEntry({ allOf: ['platform.feature_flags.resolve'] }),

  'provider_config.write': permissionEntry({ allOf: ['platform.provider_config.write'] }),
  'provider_config.default_switch': permissionEntry({ allOf: ['platform.provider_config.default_switch'] }),
  'provider_config.override': permissionEntry({ allOf: ['platform.provider_config.override'] }),
  'provider_config.delete': permissionEntry({ allOf: ['platform.provider_config.delete'] }),
  'provider_config.credentials.write': permissionEntry({ allOf: ['platform.provider_config.credentials.write'] }),

  'recovery.replay': permissionEntry({ allOf: ['platform.recovery.replay'] }),
  'recovery.manual': permissionEntry({ allOf: ['platform.recovery.manual'] }),
  'recovery.reprocess': permissionEntry({ allOf: ['platform.recovery.reprocess'] }),
  'recovery.reconcile': permissionEntry({ allOf: ['platform.recovery.reconcile'] }),
  'recovery.retry': permissionEntry({ allOf: ['platform.recovery.retry'] }),

  'runtime.charge.real.execute': permissionEntry({ allOf: ['platform.runtime.charge.real.execute'] }),
  'runtime.invoice.write': permissionEntry({ allOf: ['platform.runtime.invoice.write'] }),
  'runtime.charge.provider_status.refresh': permissionEntry({ allOf: ['platform.runtime.charge.provider_status.refresh'] }),
  'runtime.lifecycle.materializer.execute_dry_run': permissionEntry({
    allOf: ['platform.runtime.lifecycle.materializer.execute_dry_run'],
  }),
  'runtime.preview.read': permissionEntry({
    allOf: ['platform.runtime.preview.read'],
  }),
});

export const PLATFORM_MENU_GROUP_PERMISSIONS = freeze({
  overview: permissionEntry({
    anyOf: ['platform.tenants.dashboard.read', 'platform.tenants.read'],
  }),
  subscriptions: permissionEntry({
    anyOf: ['platform.subscription_requests.read', 'platform.billing.subscriptions.read'],
  }),
  billingCatalog: permissionEntry({
    anyOf: ['platform.catalog.plans.read', 'platform.catalog.features.read', 'platform.catalog.entitlements.read'],
  }),
  runtime: permissionEntry({
    anyOf: ['platform.runtime.history.read', 'platform.runtime.diagnostics.read', 'platform.runtime.scheduler.read'],
  }),
  recovery: permissionEntry({ anyOf: ['platform.recovery.read'] }),
  providerConfig: permissionEntry({ anyOf: ['platform.provider_config.read'] }),
  billingOperations: permissionEntry({ anyOf: ['platform.billing.operations.read', 'platform.billing.webhooks.read'] }),
  timeline: permissionEntry({ anyOf: ['platform.operational_events.timeline.read'] }),
  operational: permissionEntry({ anyOf: ['platform.operational_events.read', 'platform.readiness.health.read'] }),
  iam: permissionEntry({ anyOf: ['platform.iam.users.read', 'platform.iam.roles.read'] }),
  alerts: permissionEntry({ anyOf: ['platform.alerts.read'] }),
  featureFlags: permissionEntry({ anyOf: ['platform.feature_flags.read'] }),
  readiness: permissionEntry({ anyOf: ['platform.readiness.read'] }),
});

const normalizePathname = (pathname) => {
  const raw = String(pathname || '').trim();
  if (!raw) {
    return '';
  }

  const withoutHash = raw.split('#')[0];
  const withoutQuery = withoutHash.split('?')[0];
  const normalizedSlashes = withoutQuery.replace(/\/+/g, '/');

  if (!normalizedSlashes.startsWith('/')) {
    return `/${normalizedSlashes}`;
  }

  return normalizedSlashes.length > 1
    ? normalizedSlashes.replace(/\/+$/, '')
    : normalizedSlashes;
};

const escapeRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const buildRouteRegex = (routePattern, allowChildren = false) => {
  const normalized = normalizePathname(routePattern);
  const segments = normalized.split('/').filter(Boolean);
  const pathRegex = segments
    .map((segment) => (segment.startsWith(':') ? '[^/]+' : escapeRegex(segment)))
    .join('/');

  const base = `^/${pathRegex}`;
  if (allowChildren) {
    return new RegExp(`${base}(?:/.*)?$`);
  }
  return new RegExp(`${base}/?$`);
};

const routeMatchers = Object.entries(PLATFORM_ROUTE_PERMISSIONS)
  .map(([routePattern, permissionEntryValue]) => ({
    routePattern,
    permissionEntry: permissionEntryValue,
    exactRegex: buildRouteRegex(routePattern, false),
    childrenRegex: buildRouteRegex(routePattern, true),
    score: normalizePathname(routePattern).length,
  }))
  .sort((left, right) => right.score - left.score);

const toPermissionSet = (userPermissions) => {
  if (!Array.isArray(userPermissions) || userPermissions.length === 0) {
    return new Set();
  }

  return new Set(
    userPermissions
      .map((item) => String(item || '').trim())
      .filter(Boolean)
  );
};

export const hasAnyPlatformPermission = (userPermissions, requiredPermissions) => {
  const required = Array.isArray(requiredPermissions)
    ? requiredPermissions.map((item) => String(item || '').trim()).filter(Boolean)
    : [];

  if (required.length === 0) {
    return true;
  }

  const permissionSet = toPermissionSet(userPermissions);
  return required.some((permissionKey) => permissionSet.has(permissionKey));
};

export const hasAllPlatformPermissions = (userPermissions, requiredPermissions) => {
  const required = Array.isArray(requiredPermissions)
    ? requiredPermissions.map((item) => String(item || '').trim()).filter(Boolean)
    : [];

  if (required.length === 0) {
    return true;
  }

  const permissionSet = toPermissionSet(userPermissions);
  return required.every((permissionKey) => permissionSet.has(permissionKey));
};

const resolveRoutePermissionEntry = (pathname) => {
  const normalizedPath = normalizePathname(pathname);
  if (!normalizedPath) {
    return null;
  }

  return (
    routeMatchers.find((matcher) => matcher.exactRegex.test(normalizedPath))
    || routeMatchers.find((matcher) => matcher.childrenRegex.test(normalizedPath))
    || null
  );
};

const evaluatePermissionEntry = (entry, userPermissions) => {
  if (!entry) {
    return false;
  }

  return (
    hasAllPlatformPermissions(userPermissions, entry.allOf)
    && hasAnyPlatformPermission(userPermissions, entry.anyOf)
  );
};

export const canAccessPlatformRoute = (pathname, userPermissions) => {
  const routeEntry = resolveRoutePermissionEntry(pathname);
  return evaluatePermissionEntry(routeEntry?.permissionEntry, userPermissions);
};

export const canExecutePlatformAction = (actionKey, userPermissions) => {
  const normalizedActionKey = String(actionKey || '').trim();
  if (!normalizedActionKey) {
    return false;
  }

  const actionEntry = PLATFORM_ACTION_PERMISSIONS[normalizedActionKey];
  return evaluatePermissionEntry(actionEntry || null, userPermissions);
};

export const canExecutePlatformActionWithContext = ({
  actionKey,
  userPermissions,
  isPlatformAdmin = false,
  usingLegacyFallback = false,
  permissionsLoaded = true,
  hasPermissionLoadError = false,
} = {}) => {
  if (!isPlatformAdmin) {
    return false;
  }

  if (usingLegacyFallback) {
    return true;
  }

  // Fail-open transicional para preservar compatibilidade ate a consolidacao do IAM.
  if (!permissionsLoaded || hasPermissionLoadError) {
    return true;
  }

  return canExecutePlatformAction(actionKey, userPermissions);
};

export const canAccessPlatformRouteWithContext = ({
  pathname,
  userPermissions,
  isPlatformAdmin = false,
  usingLegacyFallback = false,
  permissionsLoaded = true,
  hasPermissionLoadError = false,
} = {}) => {
  if (!isPlatformAdmin) {
    return false;
  }

  if (usingLegacyFallback) {
    return true;
  }

  // Fail-open enquanto o frontend ainda convive com fallback legado e
  // a carga de permissoes pode estar indisponivel/transiente.
  if (!permissionsLoaded || hasPermissionLoadError) {
    return true;
  }

  return canAccessPlatformRoute(pathname, userPermissions);
};

export const filterPlatformMenuItemsByRouteAccess = (platformItems, platformPermissionState) => {
  if (!Array.isArray(platformItems)) {
    return [];
  }

  if (
    platformPermissionState?.loading
    || platformPermissionState?.error
    || platformPermissionState?.usingLegacyFallback
  ) {
    return [...platformItems];
  }

  const userPermissions = Array.isArray(platformPermissionState?.permissions)
    ? platformPermissionState.permissions
    : [];

  const filterItems = (items) =>
    items
      .map((item) => {
        if (Array.isArray(item?.submenu)) {
          const filteredSubmenu = filterItems(item.submenu);
          if (!filteredSubmenu.length) {
            return null;
          }
          return { ...item, submenu: filteredSubmenu };
        }

        if (item?.path && item.path.startsWith('/platform')) {
          return canAccessPlatformRoute(item.path, userPermissions) ? item : null;
        }

        return item;
      })
      .filter(Boolean);

  return filterItems(platformItems);
};
